import {
  detectOutcomeScenario,
  resolveCrmPlan,
  resolveEffectiveOutcome,
  resolveEvolutionFollowUpDateForSave,
  toLegacyReturnWorkflow,
  type EvolutionOutcome,
} from './evolutionOutcomeModel';
import type { EvolutionPlanExecutionRecord } from './evolutionPlanExecution';
import type {
  EvolutionAttachedPrintable,
  EvolutionIndicationItem,
} from './evolutionWorkflowTypes';

export interface BuildEvolutionSavePayloadInput {
  form: Record<string, any>;
  procedureLabels: string[];
  /**
   * Indicações com identidade do procedimento (categoria + natureza do ato).
   * É o que permite classificar o desfecho e escolher o funil de CRM.
   */
  indicationItems?: EvolutionIndicationItem[];
  /** Execuções de plano/orçamento registradas durante esta evolução. */
  planExecutionRecords?: EvolutionPlanExecutionRecord[];
  /** Seleções da grade da especialidade, guardadas para auditoria. */
  indicationSelections?: Record<string, { od: boolean; oe: boolean; ao: boolean }> | null;
  attachedPrintables: EvolutionAttachedPrintable[];
  indicationPrintable: EvolutionAttachedPrintable | null;
  /** Desfecho do atendimento — decide os cards de CRM. */
  outcome: EvolutionOutcome;
  /** Primeira gravação (POST) — define data/hora da evolução. */
  isCreate: boolean;
  /** Anexar desfechos de CRM (Concluir). Nunca no autosave. */
  attachCrm?: boolean;
  /** Evolução já concluída sendo editada no prazo: o autosave não a devolve a rascunho. */
  preserveStatus?: boolean;
  userId?: string;
}

/** Campos que chegam do GET (joins e anexos) e não pertencem à tabela. */
const READ_ONLY_EVOLUTION_FIELDS = [
  'additions',
  'documents',
  'images',
  'patient',
  'professional',
  'appointment',
  'unit',
  'id',
  'created_at',
  'updated_at',
];

const localYmd = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function buildEvolutionSavePayload(input: BuildEvolutionSavePayloadInput): Record<string, any> {
  const {
    form,
    procedureLabels,
    indicationItems,
    indicationSelections,
    attachedPrintables,
    indicationPrintable,
    outcome,
    isCreate,
    attachCrm = false,
    preserveStatus = false,
    userId,
  } = input;

  const payload: Record<string, any> = { ...form };
  for (const key of READ_ONLY_EVOLUTION_FIELDS) delete payload[key];
  delete payload.appointment_id;

  const detection = detectOutcomeScenario({
    indicationItems: indicationItems || [],
    printables: attachedPrintables,
  });
  const effectiveOutcome = resolveEffectiveOutcome(outcome, detection);

  if (attachCrm) {
    const followUp = resolveEvolutionFollowUpDateForSave(effectiveOutcome, detection, form.follow_up_date);
    if (followUp) payload.follow_up_date = followUp;
    else delete payload.follow_up_date;
  } else if (!payload.follow_up_date) {
    delete payload.follow_up_date;
  }

  // "Concluir" pode chegar como POST (evolução nova) ou como PUT (rascunho que o
  // autosave já criou). O backend precisa do flag para promover rascunho → assinada.
  payload.finalize = attachCrm;

  if (attachCrm) {
    const plans = resolveCrmPlan(effectiveOutcome, detection).map((plan) => ({
      funnel_type: plan.funnel_type,
      stage_hint: plan.stage_hint,
      next_action_type: plan.next_action_type,
      next_action_note: plan.next_action_note,
      next_action_at: plan.next_action_at,
      visible_after: plan.visible_after,
      notes: plan.notes,
      metadata: plan.metadata,
      owner_id: userId || undefined,
    }));

    if (plans.length) {
      payload.crm_outcomes = plans;
      // Contrato legado: back-ends antigos leem apenas este par.
      payload.crm_outcome = plans[0];
      if (plans[1]) payload.crm_outcome_secondary = plans[1];
      else delete payload.crm_outcome_secondary;
    } else {
      delete payload.crm_outcomes;
      delete payload.crm_outcome;
      delete payload.crm_outcome_secondary;
    }
  } else {
    delete payload.crm_outcomes;
    delete payload.crm_outcome;
    delete payload.crm_outcome_secondary;
  }

  const vs = payload.vital_signs || {};
  if (vs.weight && vs.height) {
    const h = vs.height / 100;
    vs.bmi = Math.round((vs.weight / (h * h)) * 10) / 10;
  }
  payload.vital_signs = vs;

  if (isCreate) {
    const now = new Date();
    payload.evolution_date = localYmd(now);
    payload.evolution_time = now.toTimeString().slice(0, 5);
  }

  payload.evolution_entry_format = 'free_text';
  payload.subjective = null;
  payload.objective = null;
  payload.assessment = null;
  payload.plan = null;

  // A identidade do procedimento (categoria e natureza do ato) é o que permite
  // classificar o desfecho; sem ela o CRM não sabe para qual funil mandar.
  const itemsByName = new Map((indicationItems || []).map((i) => [i.name, i]));
  const fromIndications = procedureLabels.map((name) => {
    const item = itemsByName.get(name);
    return {
      name,
      notes: item?.notes || '',
      procedure_id: item?.procedure_id ?? null,
      category_id: item?.category_id ?? null,
      category_name: item?.category_name ?? null,
      act_type: item?.act_type ?? null,
      source: item?.source ?? null,
    };
  });
  const fromPlan = (input.planExecutionRecords || []).map((rec) => ({ ...rec }));
  payload.procedures_performed = [...fromPlan, ...fromIndications];

  payload.metadata = {
    ...(form.metadata || {}),
    evolution_workflow: {
      printables: attachedPrintables.filter((p) => p.category !== 'indication'),
      indication: indicationPrintable,
      indication_selections: indicationSelections || null,
      outcome: effectiveOutcome,
      // Mantido no formato antigo para telas e relatórios que ainda leem daqui.
      return: toLegacyReturnWorkflow(effectiveOutcome),
    },
  };

  if (preserveStatus) {
    delete payload.status;
    delete payload.is_signed;
    delete payload.signed_at;
    delete payload.digital_signature;
  } else if (!attachCrm) {
    payload.status = 'draft';
    payload.is_signed = false;
    delete payload.signed_at;
    delete payload.digital_signature;
  }

  return payload;
}

/** Evita POST de rascunho vazio (só paciente/profissional preenchidos). */
export function hasMeaningfulEvolutionDraftContent(payload: Record<string, any>): boolean {
  if (String(payload.evolution_free_text || '').trim()) return true;
  if (String(payload.diagnosis || '').trim()) return true;
  if (String(payload.internal_notes || '').trim()) return true;
  if (String(payload.follow_up_notes || '').trim()) return true;
  const procedures = payload.procedures_performed;
  if (Array.isArray(procedures) && procedures.length > 0) return true;
  const workflow = payload.metadata?.evolution_workflow;
  if (workflow?.printables?.length) return true;
  if (workflow?.indication) return true;
  return false;
}

export function extractEvolutionIdFromResponse(res: any): string | null {
  const root = res?.data;
  const id = root?.data?.id ?? root?.id ?? root?.data?.evolution?.id;
  return id ? String(id) : null;
}
